import React, { useContext, useEffect, useState } from 'react';

import {
    Box,
} from '@mui/material';
import { useGridApiRef, GridCellEditStopReasons, gridFilteredSortedRowEntriesSelector } from '@mui/x-data-grid-pro';
import { useAbortController } from '../../contexts/AbortControllerContext';
import ProjectContext from '../../contexts/ProjectContext';
import FullDataGrid from '../datagrid/FullDataGrid';
import AleConfigMenuBar from '../menubar/AleConfigMenuBar';
import TextEditor from '../custom/TextEditor';
import {useFetch} from '../../components/auth/useFetch';

// In milliseconds
let minimumLoadingAnimationDuration = 2000;

    // Datagrid column Definition for Metadata in DB
    const metadataColumns = [
        {
            field: 'metadataName',
            headerName: 'Available Metadata',
            flex: 1
        },
        {
            field: 'count',
            headerName: 'Count',
            flex: 1
        },
    ]

    // Datagrid column Definition for ALE Config
    const configColumns = [
        {
            field: 'metadataName',
            headerName: 'ALE Metadata',
            flex: 1
        },
        {
            field: 'configName',
            headerName: 'Output Name',
            flex: 1,
            editable: true 
        }]

export default function AleConfig(props) {

    const {
        deleteConfig,
        deliverable,
        deliverableConfig,
        exportType,
        menuRef,
        metadataRef,
        openMenu,
        setDeliverableConfig,
        setMenuContent,
        submitConfig,
        undoConfig,
        savedUpdate
    } = props
    const { abortSimilarGroupRequests, registerController, unregisterController } = useAbortController()
    const projectContext = useContext(ProjectContext)

    const [configRows, setConfigRows] = useState([])
    const [metadataRows, setMetadataRows] = useState([])
    const [customRows, setCustomRows] = useState([])

    const [useTextEditor, setUseTextEditor] = useState(false)

    const configRef = useGridApiRef();
    const fetchWithAuth = useFetch()

    useEffect(() => {
        if(deliverable?.type === exportType)
        {
            retrieveMetadata()
        }
    }, [deliverable, savedUpdate])

    const retrieveMetadata = () => {
        abortSimilarGroupRequests(`deliverable.config.get`)
        const dbConfigController = registerController(`deliverable.config.get.${deliverable.type}.${deliverable.name}`)
        let url = `${process.env.REACT_APP_BACKEND_URL}/konsol/dbconfig/?config_name=${deliverable.name}&config_type=${deliverable.type}`
        fetchWithAuth(url, {
            headers: {
                'X-Project-ID': projectContext?.project_db
            },
            signal: dbConfigController.signal
        })
        .then((response) => response.json())
        .then((res) => {
            setDeliverableConfig(res)
            
            let custom = Object.entries(res?.args?.customColumn || {}).map(
                ([metadataName, customString], index) => ({
                    id: index,
                    metadataName,
                    customString
                })
            )
            setCustomRows(custom)


            let id = 0;
            const config = Object.entries(res.data).flatMap(
                ([configName, metadataNames]) => 
                metadataNames.map((metadataName) => ({ 
                    id: id++,
                    metadataName,
                    configName
                }))
            )
            setConfigRows(config)
            return config
        })
        .catch((e) => {
            if(e.name !== 'AbortError'){
                console.error(e);
                setDeliverableConfig({})
                setConfigRows([])
                setCustomRows([])
            }
        })
        .then((config) => {
            return config.reduce((acc, obj) => {
                acc[obj.metadataName] = (acc[obj.metadataName] || 0) + 1;
                return acc;
            }, {})
        })
        .catch((error) => {
            return 0
        })
        .then((count) => {
            const metadataController = registerController(`deliverable.config.get.metadata`)
            let url = `${process.env.REACT_APP_BACKEND_URL}/konsol/metadata`
            fetchWithAuth(url, {
                headers:{
                    'X-Project-ID': projectContext?.project_db
                },
                signal: metadataController.signal
            })
                .then((response) => response.json())
                .then((res) => {
                    setMetadataRows(() => {
                        return res.map((item, index) => {
                            return { 
                                id: index, 
                                metadataName: item,
                                count: count[item] !== undefined ? count[item] : 0
                            }
                        })
                    })
                })
                .catch(e => {
                    if(e.name !== 'AbortError'){
                        console.error(e);
                    }
                })
                .finally(() => unregisterController(metadataController, `deliverable.config.get.metadata`))
        })
        .catch(e => {
            if(e.name !== 'AbortError'){
                console.error(e);
            }
        })
        .finally(() => unregisterController(dbConfigController, `deliverable.config.get.${deliverable.type}.${deliverable.name}`))
    }

    const addConfigRowOnClick = (e) => {
        const sortedRows = configRef.current.getSortedRowIds()
        const row = {
            id: sortedRows.length > 0 ? sortedRows[sortedRows.length - 1] + 1 : 0,
            metadataName: e.row.metadataName,
            configName: e.row.metadataName
        }
        metadataRef.current.updateRows([{...e.row, count: ++e.row.count}])
        setConfigRows((prev) => {
            return [...prev, row]
        })
    }

    const addVisibleConfigRows = () => {
        const configRows = configRef.current.getSortedRowIds()
        let id = configRows.length > 0 ? configRows[configRows.length - 1] + 1 : 0

        metadataRef.current.updateRows(
            gridFilteredSortedRowEntriesSelector(metadataRef)
            .map((item) => {
                return {...item.model, count: ++item.model.count}
            })
        )

        setConfigRows((prev) => {
            return [...prev, ...gridFilteredSortedRowEntriesSelector(metadataRef)
                .map((item) => {
                    return {
                        id: id++,
                        metadataName: item.model.metadataName,
                        configName: item.model.metadataName
                    }
                })
            ]
        })
    }

    const deleteConfigRows = (row, e) => {
        //If a delete/backspace button is pressed
        //46 = Delete Key
        //8 = Backspace Key
        if(row.cellMode === "view" && (e.keyCode === 46 || e.keyCode === 8)){
            const rowsToDelete = [...configRef.current.getSelectedRows().keys()]
            setConfigRows((prev) => {
                const reOrderRows = [...prev].filter((_, index) => !rowsToDelete.includes(index))
                reOrderIDs(reOrderRows, Math.min(...rowsToDelete))
                return reOrderRows
            })
        }
    }

    const handleCellEditStop = (params, e) => {
        if (params.reason === GridCellEditStopReasons.cellFocusOut) {
            e.defaultMuiPrevented = true;
        }
        else{
            params.row.configName = e.target.value || ''
        }
    }

    const handleRowChange = (params) => {
        setConfigRows((prev) => {
            const reOrderRows = [...prev]
            const row = reOrderRows.splice(params.oldIndex, 1)[0]
            reOrderRows.splice(params.targetIndex, 0, row)
            reOrderIDs(reOrderRows, Math.min(params.oldIndex, params.targetIndex))
            return reOrderRows
        })
        configRef.current.setRowSelectionModel([])
    }

    const reOrderIDs = (rowArr, startIndex) => {
        return rowArr.slice(startIndex).forEach((item, index) => {
            item.id = startIndex + index
        })
    }

    

    const handleArgsChange = (value) => {
        setDeliverableConfig(value)
    };

    return (
        <React.Fragment>
            {deliverable?.type === exportType && (
                <>
                <Box
                    display='flex'
                    flexDirection={'row'} 
                    flex={0.35}
                    height='100%'
                    width='35%'                    
                    // border='1px solid red'
                >
                    <FullDataGrid
                        apiRef={metadataRef}
                        rows={metadataRows}
                        columns={metadataColumns}
                        onRowClick={(e) => addConfigRowOnClick(e)}
                        disableSelection
                    />
                </Box>
                <Box
                    display='flex'
                    flexDirection={'row'} 
                    flex={0.65}
                    height='100%'
                    width='65%'
                    // border='1px solid red'
                >
                    {
                        useTextEditor 
                        ?
                            <TextEditor
                                configText={JSON.stringify(deliverableConfig)}
                            />
                        :
                            <FullDataGrid
                                // loading={loading}
                                apiRef={configRef}
                                rows={configRows}
                                columns={configColumns}
                                rowReordering={true}
                                onRowOrderChange={handleRowChange}
                                onCellEditStop={handleCellEditStop}
                                onCellKeyDown={deleteConfigRows}
                            />
                    }
                </Box>
                <AleConfigMenuBar
                    addVisibleConfigRows={addVisibleConfigRows}
                    config={deliverableConfig}
                    configRef={configRef}
                    customRows={customRows}
                    deleteConfig={deleteConfig}
                    handleChange={handleArgsChange}
                    menuRef={menuRef}
                    openMenu={openMenu}
                    setConfigRows={setConfigRows}
                    setCustomRows={setCustomRows}
                    setMenuContent={setMenuContent}
                    submitConfig={submitConfig}
                    setUseTextEditor={setUseTextEditor}
                    undoConfig={undoConfig}
                />
                </>
            )}
        </React.Fragment>
    )
}